import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
} from "@mui/material";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Typography from "@mui/material/Typography";
import { ticketsActivityLog, ticketsApi } from "../../../services/TicketsServices";
import RefundRequestDialog from "../TicketManagement/shared/RefundRequestDialog";

export default function RefundsPanel() {
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [stateFilter, setStateFilter] = useState("refund_requested");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const getLatestRefundNoteFromLogs = (logs) => {
    const match = (logs || []).find((l) => ["request_refund", "approve_refund", "reject_refund"].includes(l?.transition));
    return match?.description || "";
  };

  const formatStatusLabel = (value) => {
    if (!value) return "-";
    return String(value)
      .split("_")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const columns = useMemo(
    () => [
      { key: "ticket_no", label: "Ticket No." },
      { key: "museum", label: "Museum" },
      { key: "requested_by", label: "Requested By" },
      { key: "remarks", label: "Remarks" },
      { key: "status", label: "Status" },
    ],
    []
  );

  useEffect(() => {
    fetchTicketList();
  }, [stateFilter, page, perPage]);

  const fetchTicketList = () => {
    ticketsApi("get", null, { state: stateFilter, page, per_page: perPage }).then(async (res) => {
      const data = res?.data || [];

      const notesById = {};
      await Promise.all(
        data
          .filter((t) => Boolean(t?.id))
          .map(async (t) => {
            try {
              const logRes = await ticketsActivityLog("get", t.id);
              notesById[t.id] = getLatestRefundNoteFromLogs(logRes?.data);
            } catch (e) {
              notesById[t.id] = "";
            }
          })
      );

      const nextRows = data.map((t) => {
        const refundNote = notesById[t?.id] || "";
        return {
          id: t?.id,
          ticket_no: t?.ticket_no,
          museum: t?.museum_name,
          requested_by: t?.user_name,
          remarks: refundNote || "-",
          reason: refundNote || "-",
          status: t?.state,
          raw: t,
        };
      });

      setRows(nextRows);
      setTotal(res?.meta?.total ?? data.length);
    });
  };

  const handleReview = (row) => {
    setEditRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditRow(null);
  };

  const handleDecision = (payload) => {
    fetchTicketList();
    handleClose();
  };

  const handleChangePage = (_, nextPage) => {
    setPage(nextPage);
  };

  const handleChangePerPage = (event) => {
    const next = Number(event?.target?.value || 10);
    setPerPage(next);
    setPage(1);
  };

  const displayRows = useMemo(() => {
    return rows.map((r) => ({
      ...r,
      status: (
        <Chip
          size={"small"}
          label={formatStatusLabel(r.status)}
          color={
            r.status === "approved" || r.status === "refund_approved"
              ? "success"
              : r.status === "rejected" || r.status === "refund_rejected"
                ? "error"
                : "warning"
          }
        />
      ),
    }));
  }, [rows]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <FormControl size={"small"} sx={{ minWidth: 220 }}>
          <InputLabel id="refund-state-filter-label">Status</InputLabel>
          <Select
            labelId="refund-state-filter-label"
            label="Status"
            value={stateFilter}
            onChange={(e) => {
              setPage(1);
              setStateFilter(e.target.value);
            }}
          >
            <MenuItem value={"refund_requested"}>Requested</MenuItem>
            <MenuItem value={"refund_rejected"}>Rejected</MenuItem>
            <MenuItem value={"refunded"}>Approved</MenuItem>
            <MenuItem value={"expired"}>Expired</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer className={"box-shadow"} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="table">
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ color: "#B3B3B3", width: 20 }}>
                Sl.No
              </TableCell>
              {columns.map((c) => (
                <TableCell key={c.key} align="left" sx={{ color: "#B3B3B3" }}>
                  {c.label}
                </TableCell>
              ))}
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows?.length > 0 ? (
              displayRows.map((r, idx) => (
                <TableRow hover key={r.id || idx}>
                  <TableCell>{idx + 1}</TableCell>
                  {columns.map((c) => (
                    <TableCell key={`${r.id}_${c.key}`} sx={{ color: "#3D3D3D" }}>
                      {r?.[c.key] ?? "-"}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    {/* Review functionality disabled for receptionist role */}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 2} style={{ textAlign: "center" }}>
                  <Typography style={{ color: "red" }}>No records</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <TablePagination
          component={"div"}
          count={Number.isFinite(total) ? total : 0}
          page={Math.max(0, (page || 1) - 1)}
          onPageChange={(e, nextPage) => handleChangePage?.(e, nextPage + 1)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => handleChangePerPage?.(e)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
        />
      </Box>

      {open && <RefundRequestDialog open={open} row={editRow} onClose={handleClose} onSubmit={handleDecision} />}
    </Box>
  );
}
